import { ConnectionForm } from '@/components/connection-form';
import { Card } from '@/components/ui';
import { useConnection } from '@/hooks/use-connection';
import type { Connection, ProbeOutcome } from '@/lib/connection';

export interface SetupScreenProps {
  readonly initial?: Connection;
  readonly outcome?: ProbeOutcome;
}

function Code({ children }: { readonly children: string }) {
  return <code className="rounded-sm bg-surface-2 px-1 py-px font-mono text-[11px] text-ink">{children}</code>;
}

/**
 * What a visitor sees before the console knows where their service is.
 *
 * This copy carries the whole explanation of what the page is, because for the hosted build
 * it is the first and possibly only thing anyone reads. It says what the page does with the
 * address — talks to it from this browser, sends it nowhere else — since asking someone to
 * point a page at the service that books their trades is only reasonable if the page says
 * what it is going to do.
 */
export function SetupScreen({ initial, outcome }: SetupScreenProps) {
  const { connect } = useConnection();

  return (
    <div className="flex justify-center px-3 py-8 sm:py-16">
      <Card title="Connect to your Fleece service" className="w-full max-w-md">
        <div className="flex flex-col gap-4 px-3 py-3">
          <p className="text-xs text-ink-2">
            This console is a static page. It holds no data and knows nothing about your accounts until you tell it where your Fleece service is listening — then it talks to that
            address directly from this browser, and nowhere else.
          </p>

          <ConnectionForm initial={initial} initialOutcome={outcome} onConnect={connect} />

          <div className="flex flex-col gap-2 border-t border-line pt-3 text-[11px] text-muted">
            <p>
              <span className="font-medium text-ink-2">Running it on this machine?</span> <Code>npm start</Code> serves the API on <Code>http://127.0.0.1:3100</Code>. Chrome asks
              your permission before a hosted page reaches it; Safari refuses a plain <Code>http://</Code> address from an HTTPS page entirely, so use Chrome or Firefox, or serve
              this console locally with <Code>npm run start:web</Code>.
            </p>
            <p>
              <span className="font-medium text-ink-2">Reachable over HTTPS?</span> Any address with a certificate works from any device. The service&rsquo;s{' '}
              <Code>server.corsOrigins</Code> must let <Code>{window.location.origin}</Code> through; its default, <Code>*</Code>, does.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
